import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    AdminReviewSchema,
    AdminReviewsStatsSchema,
    ReviewModerationResponseSchema,
    AdminReviewUpdateResponseSchema,
    AdminReviewsQueryParams,
    AdminUpdateReviewBody,
    PublishReviewBody,
    UnpublishReviewBody,
    DeleteReviewBody,
    ProjectValueRangesResponseSchema,
} from "../schemas/reviews-v2";

export function createAdminReviewsV2Api(http: HttpClient, baseUrl: string) {
    const adminBaseUrl = `${baseUrl}/admin/reviews-v2`;
    
    return {
        getAllReviews: createEndpointWithPagination<
            z.infer<typeof AdminReviewSchema>,
            z.infer<typeof AdminReviewsStatsSchema>,
            unknown,
            AdminReviewsQueryParams
        >(http, "GET", "", {
            base: adminBaseUrl,
            itemSchema: AdminReviewSchema,
            metaSchema: AdminReviewsStatsSchema,
        }),

        getPendingReviews: createEndpointWithPagination<
            z.infer<typeof AdminReviewSchema>,
            z.infer<typeof AdminReviewsStatsSchema>,
            unknown,
            AdminReviewsQueryParams
        >(http, "GET", "/pending", {
            base: adminBaseUrl,
            itemSchema: AdminReviewSchema,
            metaSchema: AdminReviewsStatsSchema,
        }),

        getPublishedReviews: createEndpointWithPagination<
            z.infer<typeof AdminReviewSchema>,
            z.infer<typeof AdminReviewsStatsSchema>,
            unknown,
            AdminReviewsQueryParams
        >(http, "GET", "/published", {
            base: adminBaseUrl,
            itemSchema: AdminReviewSchema,
            metaSchema: AdminReviewsStatsSchema,
        }),

        getProjectValueRanges: createEndpoint<
            z.infer<typeof ProjectValueRangesResponseSchema>,
            unknown,
            QueryParams
        >(http, "GET", "/project-value-ranges", {
            base: adminBaseUrl,
            responseSchema: ProjectValueRangesResponseSchema,
        }),

        publishReview: createEndpoint<
            z.infer<typeof ReviewModerationResponseSchema>,
            PublishReviewBody,
            QueryParams,
            { review: number }
        >(http, "POST", "/:review/publish", {
            base: adminBaseUrl,
            responseSchema: ReviewModerationResponseSchema,
            params: ["review"],
        }),

        unpublishReview: createEndpoint<
            z.infer<typeof ReviewModerationResponseSchema>,
            UnpublishReviewBody,
            QueryParams,
            { review: number }
        >(http, "POST", "/:review/unpublish", {
            base: adminBaseUrl,
            responseSchema: ReviewModerationResponseSchema,
            params: ["review"],
        }),

        deleteReview: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            DeleteReviewBody,
            QueryParams,
            { review: number }
        >(http, "DELETE", "/:review", {
            base: adminBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["review"],
        }),

        setShortTextVisibility: createEndpoint<
            z.infer<typeof AdminReviewSchema>,
            { short_text_grandfathered: boolean },
            QueryParams,
            { review: number }
        >(http, "PUT", "/:review/short-text-visibility", {
            base: adminBaseUrl,
            responseSchema: AdminReviewSchema,
            params: ["review"],
        }),

        updateReview: createEndpoint<
            z.infer<typeof AdminReviewUpdateResponseSchema>,
            AdminUpdateReviewBody,
            QueryParams,
            { review: number }
        >(http, "PUT", "/:review", {
            base: adminBaseUrl,
            responseSchema: AdminReviewUpdateResponseSchema,
            params: ["review"],
        }),
    };
}
